import {Directive, ElementRef, OnDestroy, OnInit, Renderer2} from '@angular/core';

@Directive({
    selector: '[appResizableIframe]',
    standalone: false
})
export class ResizableIframeDirective implements OnInit, OnDestroy {
    private resizeHandle!: HTMLElement;
    private unlistenMousemove: (() => void) | null = null; // Unlisten function for mousemove
    private unlistenMouseup: (() => void) | null = null; // Unlisten function for mouseup
    private unlistenTouchmove: (() => void) | null = null; // Unlisten function for touchmove
    private unlistenTouchend: (() => void) | null = null; // Unlisten function for touchend

    constructor(
        private el: ElementRef,
        private renderer: Renderer2
    ) {
    }

    ngOnInit(): void {
        const parent: any = this.renderer.parentNode(this.el.nativeElement);

        if (parent) {
            const computedStyle: CSSStyleDeclaration = window.getComputedStyle(parent);
            // Check if the current position is static
            // If it's static set it to relative to allow positioning of the handle
            if (computedStyle.position === 'static') {
                this.renderer.setStyle(parent, 'position', 'relative');
            }
        }

        this.resizeHandle = this.renderer.createElement('div');
        this.renderer.addClass(this.resizeHandle, 'resizeHandle');
        this.renderer.setAttribute(this.resizeHandle, 'aria-label', 'Height Resize Handle')
        this.renderer.setAttribute(this.resizeHandle, 'title', 'Height Resize Handle')

        // Add icon
        let handleIcon: HTMLElement = this.renderer.createElement('span');
        this.renderer.addClass(handleIcon, 'iframeHandleIcon');
        this.renderer.appendChild(this.resizeHandle, handleIcon)

        this.renderer.appendChild(parent, this.resizeHandle);
        // Listen for mousedown and touchstart events on the resize handle
        this.renderer.listen(this.resizeHandle, 'mousedown', this.onStartDrag.bind(this));
        this.renderer.listen(this.resizeHandle, 'touchstart', this.onStartDrag.bind(this));
    }

    ngOnDestroy(): void {
        this.onEndDrag(); // Ensure all event listeners are cleaned up when the directive is destroyed
        if (this.resizeHandle) {
            // Remove the resize handle from the DOM
            this.renderer.removeChild(this.renderer.parentNode(this.resizeHandle), this.resizeHandle);
        }
    }

    private onStartDrag(event: MouseEvent | TouchEvent): void {
        // Prevent default drag behavior
        event.preventDefault();

        if (event instanceof MouseEvent && event.button !== 0) {
            return; // Exit if not a left-click
        }

        const iframeElement: HTMLIFrameElement = this.el.nativeElement;
        const startY: number = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
        const startHeight: number = iframeElement.offsetHeight;

        // Temporarily disable pointer events on the iframe this stops bug where mouseup wasn't detected on iframe
        this.renderer.setStyle(iframeElement, 'pointer-events', 'none');

        // Attach listeners to the document
        this.unlistenMousemove = this.renderer.listen('document', 'mousemove', (e: MouseEvent): void =>
            this.onDrag(e, startY, startHeight, iframeElement)
        );
        this.unlistenTouchmove = this.renderer.listen('document', 'touchmove', (e: TouchEvent): void =>
            this.onDrag(e, startY, startHeight, iframeElement)
        );

        this.unlistenMouseup = this.renderer.listen('document', 'mouseup', this.onEndDrag.bind(this));
        this.unlistenTouchend = this.renderer.listen('document', 'touchend', this.onEndDrag.bind(this));
    }

    private onDrag(event: MouseEvent | TouchEvent, startY: number, startHeight: number, iframeElement: HTMLElement): void {
        event.preventDefault();

        const currentY: number = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
        const deltaY: number = currentY - startY;
        const newHeight: number = Math.max(50, startHeight + deltaY); // Minimum height of 50px
        this.renderer.setStyle(iframeElement, 'height', `${newHeight}px`);
    }

    private onEndDrag(): void {
        const iframeElement: HTMLIFrameElement = this.el.nativeElement;

        // Re-enable pointer events on the iframe
        this.renderer.setStyle(iframeElement, 'pointer-events', 'auto');

        // Remove listeners
        if (this.unlistenMousemove) {
            this.unlistenMousemove();
            this.unlistenMousemove = null;
        }
        if (this.unlistenMouseup) {
            this.unlistenMouseup();
            this.unlistenMouseup = null;
        }
        if (this.unlistenTouchmove) {
            this.unlistenTouchmove();
            this.unlistenTouchmove = null;
        }
        if (this.unlistenTouchend) {
            this.unlistenTouchend();
            this.unlistenTouchend = null;
        }
    }
}
